import { cadOpType } from "../factory";
import { createBufferCone, createCone } from "../../geometries/solid/cone";
import { IPoint } from "../../math/types";
import { vectorDist2D } from "../../math/distance";
import { copyIPoint } from "../../math/point";
import { Parameter3dOP } from "./parameter-3d";
import { ConeCommand } from "../../commands/primitives/cone";
import { settingsOpModes } from "../step-operations";
import { getCurrentSolidMaterial } from "lib/materials";
import { addEulerAnglesToEulerAngles } from "lib/math/plane";

export class ConeOP extends Parameter3dOP {
  public opType = cadOpType.CYLINDER;
  protected static sampleData = { upperRadius: 0.1, lowerRadius: 1, height: 1 };

  constructor() {
    super();
    this.offSet.z = ConeOP.sampleData.height * 0.5;
  }
  protected iniSettingsOp() {
    this.settingsOpManager.setCfg([{
      infoMsg: "Insert center: ",
      stepMode: settingsOpModes.DEFAULTXYZ,
      cmdLineListener: this.addPointFromExt.bind(this),
      endStepCallback: () => {
        this.basePoint = copyIPoint(this.lastPoint);
        const planeManager = this.graphicProcessor.getPlaneManager();
        planeManager.activePlane.position = this.basePoint;
        planeManager.activePlane.locked = true;
      }
    }, {
      infoMsg: "Insert radius: ",
      stepMode: settingsOpModes.ONEBOX,
      currValue: () => (ConeOP.sampleData.lowerRadius).toFixed(3),
      cmdLineListener: (cmd: string) => {
        const r = parseFloat(cmd);
        if (r) {
          ConeOP.sampleData.lowerRadius = r;
          this.setNextStep();
          this.numPoints++;
          this.recalculateObj();
        }
      },
      endStepCallback: () => {
        const planeManager = this.graphicProcessor.getPlaneManager();
        planeManager.activePlane.rotation = addEulerAnglesToEulerAngles({ x: Math.PI * 0.5, y: 0, z: 0 }, this.rotation);
      }
    }, {
      infoMsg: "Insert height: ",
      stepMode: settingsOpModes.ONEBOX,
      currValue: () => (ConeOP.sampleData.height).toFixed(3),
      cmdLineListener: (cmd: string) => {
        const h = parseFloat(cmd);
        if (h) {
          ConeOP.sampleData.height = h;
          this.offSet.z = ConeOP.sampleData.height * 0.5;
          this.setNextStep();
          this.numPoints++;
          this.recalculateObj();
        }
      },
      endStepCallback: () => {
        const { x, y, z } = this.rotation;
        const planeManager = this.graphicProcessor.getPlaneManager();
        planeManager.activePlane.position = copyIPoint(this.lastPoint);
        planeManager.activePlane.rotation = { x, y, z };
      }
    }, {
      infoMsg: "Insert upper radius: ",
      stepMode: settingsOpModes.ONEBOX,
      currValue: () => (ConeOP.sampleData.upperRadius).toFixed(3),
      cmdLineListener: (cmd: string) => {
        const h = parseFloat(cmd);
        if (h !== undefined && !isNaN(h)) {
          ConeOP.sampleData.upperRadius = h;
          this.endOperation();
        }
      },
      endStepCallback: this.endOperation.bind(this),
    },]);
  }
  protected createAux() {
    return createCone(
      ConeOP.sampleData.upperRadius,
      ConeOP.sampleData.lowerRadius,
      ConeOP.sampleData.height
    );
  }
  protected createGeom() {
    return createBufferCone(
      ConeOP.sampleData.upperRadius,
      ConeOP.sampleData.lowerRadius,
      ConeOP.sampleData.height
    );
  }

  public moveLastPoint(point: IPoint) {
    if (this.numPoints === 0) {
      this.basePoint = copyIPoint(point);
      const planeManager = this.graphicProcessor.getPlaneManager();
      this.rotation = planeManager.activePlane.rotation;
      this.recalculateObj();

    } else if (this.numPoints === 1) {
      const planeManager = this.graphicProcessor.getPlaneManager();
      const p1 = planeManager.activePlane.getRelativePoint(point);
      ConeOP.sampleData.lowerRadius = vectorDist2D({ x: 0, y: 0, z: 0 }, p1);
      this.recalculateObj();

    } else if (this.numPoints === 2) {
      const planeManager = this.graphicProcessor.getPlaneManager();
      const p1 = planeManager.activePlane.getRelativePoint(point);
      ConeOP.sampleData.height = p1.y;
      this.offSet.z = ConeOP.sampleData.height * 0.5;
      this.recalculateObj();

    } else if (this.numPoints === 3) {
      const planeManager = this.graphicProcessor.getPlaneManager();
      const p1 = planeManager.activePlane.getRelativePoint(point);
      ConeOP.sampleData.upperRadius = vectorDist2D({ x: 0, y: 0, z: 0 }, p1);
      this.recalculateObj();
    }
  }

  public save(): void {
    if (this.graphicProcessor) {
      const command = new ConeCommand(
        ConeOP.sampleData.upperRadius,
        ConeOP.sampleData.lowerRadius,
        ConeOP.sampleData.height,
        this.basePoint,
        this.rotation,
        this.offSet,
        { x: 1, y: 1, z: 1 },
        this.getCurrentSceneId(),
        this.graphicProcessor,
        getCurrentSolidMaterial()
      );
      if (command) this.graphicProcessor.storeAndExecute(command);
    }
  }
}
